import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';

const EMPTY_FORM = { name: '', company_name: '', address_line1: '', address_line2: '', address_line3: '', city: '', county: '', postcode: '', contact_name: '', is_default: false };

export default function DeliveryAddressPage() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);

  const load = async () => {
    setLoading(true);
    try { setAddresses(await api.get('delivery-addresses', { all: '1' })); }
    catch { setAddresses([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setError(null);
    setShowForm(true);
  };

  const startEdit = (a) => {
    setForm({
      name: a.name,
      company_name: a.company_name || '',
      address_line1: a.address_line1 || '',
      address_line2: a.address_line2 || '',
      address_line3: a.address_line3 || '',
      city: a.city || '',
      county: a.county || '',
      postcode: a.postcode || '',
      contact_name: a.contact_name || '',
      is_default: !!a.is_default,
    });
    setEditId(a.id);
    setError(null);
    setShowForm(true);
  };

  const cancel = () => { setShowForm(false); setEditId(null); setError(null); };

  const save = async () => {
    if (!form.name.trim() || !form.address_line1.trim()) {
      setError('Name and Address Line 1 are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editId) {
        await api.put(`delivery-addresses/${editId}`, form);
      } else {
        await api.post('delivery-addresses', form);
      }
      setShowForm(false);
      setEditId(null);
      load();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
    setSaving(false);
  };

  const deactivate = async (a) => {
    if (!confirm(`Remove "${a.name}"? It will no longer appear in PO dropdown lists.`)) return;
    await api.delete(`delivery-addresses/${a.id}`);
    load();
  };

  const reactivate = async (a) => {
    await api.put(`delivery-addresses/${a.id}`, { is_active: 1 });
    load();
  };

  return (
    <div className="fade-in lg:col-span-3">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">Delivery Addresses</h2>
          <div className="text-[9px] text-gray-500">Addresses available when creating a Purchase Order</div>
        </div>
        <button onClick={openAdd} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
          + Add Address
        </button>
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <h3 className="text-[10px] font-bold text-gray-700 mb-3 uppercase tracking-wide">
            {editId ? 'Edit Address' : 'New Address'}
          </h3>
          {error && <div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 mb-3">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            {/* Internal-only label */}
            <div className="col-span-2 rounded border border-gray-200 bg-white p-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name / Label * <span className="font-normal text-gray-400 normal-case">— internal only, NOT printed on the PO</span></label>
              <input className="form-input" placeholder="e.g. RRSL Raynesway" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
            </div>

            {/* Everything below prints on the PO */}
            <div className="col-span-2 -mb-1">
              <span className="text-[8px] font-bold uppercase tracking-wide text-navy">Delivery address — printed on the PO</span>
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Company / Site Name</label>
              <input className="form-input" placeholder="e.g. Rolls-Royce Submarines, REL Stores" value={form.company_name} onChange={e => set('company_name', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address Line 1 *</label>
              <input className="form-input" value={form.address_line1} onChange={e => set('address_line1', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address Line 2</label>
              <input className="form-input" value={form.address_line2} onChange={e => set('address_line2', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address Line 3</label>
              <input className="form-input" value={form.address_line3} onChange={e => set('address_line3', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">City</label>
              <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">County</label>
              <input className="form-input" value={form.county} onChange={e => set('county', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Postcode</label>
              <input className="form-input" value={form.postcode} onChange={e => set('postcode', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">FAO / Contact Name <span className="font-normal text-gray-400 normal-case">(prints as “FAO:”)</span></label>
              <input className="form-input" placeholder="e.g. IT Stores" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} />
            </div>

            {/* Live preview of exactly what prints on the PO */}
            <div className="col-span-2">
              <div className="text-[8px] font-bold uppercase text-gray-400 mb-1">How it appears on the PO (SHIP TO)</div>
              <div className="text-[10px] text-gray-700 bg-white border border-gray-200 rounded px-3 py-2 whitespace-pre-line min-h-[40px]">
                {[form.company_name, form.address_line1, form.address_line2, form.address_line3, form.city, form.county, 'United Kingdom', form.postcode]
                  .filter(Boolean).join('\n') || <span className="text-gray-300 italic">Fill in the address above…</span>}
                {form.contact_name ? `\nFAO: ${form.contact_name}` : ''}
              </div>
            </div>

            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="is-default-chk" checked={!!form.is_default} onChange={e => set('is_default', e.target.checked)} />
              <label htmlFor="is-default-chk" className="text-[9px] text-gray-600">Set as default delivery address (pre-selected when creating a new PO)</label>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button onClick={cancel} className="px-3 py-1 text-[9px] border border-gray-200 rounded bg-white hover:bg-gray-50">Cancel</button>
            <button
              onClick={save}
              disabled={saving || !form.name.trim() || !form.address_line1.trim()}
              className="px-4 py-1 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-40 hover:bg-navy/80"
            >
              {saving ? 'Saving…' : editId ? 'Save Changes' : 'Add Address'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        {loading ? (
          <div className="text-center py-10 text-[10px] text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold">Name</th>
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold">Address</th>
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold">Contact</th>
                <th className="px-3 py-2 text-center text-[8px] uppercase text-gray-500 font-bold">Default</th>
                <th className="px-3 py-2 text-center text-[8px] uppercase text-gray-500 font-bold">Status</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {addresses.map(a => (
                <tr key={a.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!a.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2.5">
                    <div className="text-[10px] font-semibold text-navy">{a.name}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="text-[9px] text-gray-700">{a.address_line1}</div>
                    {a.address_line2 && <div className="text-[9px] text-gray-500">{a.address_line2}</div>}
                    <div className="text-[9px] text-gray-500">{[a.city, a.county, a.postcode].filter(Boolean).join(', ')}</div>
                  </td>
                  <td className="px-3 py-2.5 text-[9px] text-gray-500">{a.contact_name || '—'}</td>
                  <td className="px-3 py-2.5 text-center">
                    {a.is_default
                      ? <span className="text-[8px] font-bold bg-gold/20 text-gold px-1.5 py-0.5 rounded">DEFAULT</span>
                      : <span className="text-[8px] text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${a.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(a)} className="text-[9px] text-gold hover:underline mr-3">Edit</button>
                    {a.is_active
                      ? !a.is_default && <button onClick={() => deactivate(a)} className="text-[9px] text-red-400 hover:text-red-600">Remove</button>
                      : <button onClick={() => reactivate(a)} className="text-[9px] text-emerald-600 hover:underline">Reactivate</button>}
                  </td>
                </tr>
              ))}
              {addresses.length === 0 && (
                <tr><td colSpan="6" className="text-center py-10 text-[10px] text-gray-400">
                  No delivery addresses yet — click "+ Add Address" to get started.
                </td></tr>
              )}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="mt-3 text-[8px] text-gray-400 bg-blue-50 border border-blue-100 rounded px-3 py-2">
        These addresses appear in the <strong>Delivery Address</strong> dropdown when creating or editing a Purchase Order.
        The <strong>default</strong> address is pre-selected for convenience.
        Removing an address marks it inactive — it will no longer appear in new POs but is preserved on existing ones.
      </div>
    </div>
  );
}
